import type { ChipOption } from '@/components/ChipSelect';
import { TASK_PRIORITY_LABELS, type TaskCard, type TaskKind, type TaskPriority, type TaskPrStatus, type TaskSize, type TaskType } from '@shared/types/tasks';
import type { StatusMeta } from '@/components/status-tone';
import type { PrMergeView } from '@shared/github-pr';

/** A task's type: its own, else the Board's default (a card made before the types loaded). */
export function taskTypeOf(types: TaskType[], task: Pick<TaskCard, 'type_id'>): TaskType | undefined {
  return types.find((t) => t.id === task.type_id) ?? types.find((t) => t.is_default);
}

export const TASK_SIZE_LABELS: Record<TaskSize, string> = { XS: 'XS', S: 'S', M: 'M', L: 'L', XL: 'XL' };

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  fix: 'Fix',
  performance: 'Performance',
  feature: 'Feature',
  chore: 'Chore',
  docs: 'Docs',
  refactor: 'Refactor',
  ci: 'CI',
};

export const TASK_FIELD_LABELS = { size: 'Size', kind: 'Kind', priority: 'Priority', prs: 'Pull requests' } as const;

/** SF-T-151: a task with no priority, in PrioritySelect. */
export const NO_PRIORITY = 'none';

/** SF-T-151: each priority as a status (label and tone: Urgent danger to Low neutral), and "Priority" for none. */
export const TASK_PRIORITY_STATUSES: Record<TaskPriority | typeof NO_PRIORITY, StatusMeta> = {
  [NO_PRIORITY]: { label: 'Priority', tone: 'neutral' },
  urgent: { label: TASK_PRIORITY_LABELS.urgent, tone: 'danger' },
  high: { label: TASK_PRIORITY_LABELS.high, tone: 'warning' },
  medium: { label: TASK_PRIORITY_LABELS.medium, tone: 'info' },
  low: { label: TASK_PRIORITY_LABELS.low, tone: 'neutral' },
};

/** A pull request's status, coloured as everywhere else (status-tone). */
export const TASK_PR_STATUSES: Record<TaskPrStatus, StatusMeta> = {
  draft: { label: 'Draft', tone: 'neutral' },
  open: { label: 'Open', tone: 'info' },
  merged: { label: 'Merged', tone: 'success' },
  closed: { label: 'Closed', tone: 'danger' },
};

/** A pull request as GitHub sees it now, on a card (T-3). Conflicts are red. */
export const PR_MERGE_VIEWS: Record<PrMergeView, StatusMeta> = {
  ready: { label: 'Ready to merge', tone: 'success' },
  conflicts: { label: 'Conflicts', tone: 'danger' },
  blocked: { label: 'Blocked', tone: 'warning' },
  behind: { label: 'Behind base', tone: 'warning' },
  checking: { label: 'Checking…', tone: 'neutral' },
  draft: { label: 'Draft', tone: 'neutral' },
  merged: { label: 'Merged', tone: 'success' },
  closed: { label: 'Closed', tone: 'neutral' },
};

/** "#225" for a GitHub pull request address, else the address without its scheme. */
export function prLabel(url: string): string {
  const number = /\/pull\/(\d+)/.exec(url)?.[1];
  return number ? `#${number}` : url.replace(/^https?:\/\//, '');
}

/** A size as a chip, on the task and its card: cool to hot as the work grows. */
export const TASK_SIZE_CHIPS: Record<TaskSize, ChipOption> = {
  XS: { label: 'Size XS', colour: 'teal' },
  S: { label: 'Size S', colour: 'green' },
  M: { label: 'Size M', colour: 'amber' },
  L: { label: 'Size L', colour: 'orange' },
  XL: { label: 'Size XL', colour: 'red' },
};

/** A kind as a chip: a fix in red, a feature in blue, upkeep in quieter colours. */
export const TASK_KIND_CHIPS: Record<TaskKind, ChipOption> = {
  fix: { label: TASK_KIND_LABELS.fix, colour: 'red' },
  performance: { label: TASK_KIND_LABELS.performance, colour: 'amber' },
  feature: { label: TASK_KIND_LABELS.feature, colour: 'blue' },
  chore: { label: TASK_KIND_LABELS.chore, colour: 'gray' },
  docs: { label: TASK_KIND_LABELS.docs, colour: 'teal' },
  refactor: { label: TASK_KIND_LABELS.refactor, colour: 'violet' },
  ci: { label: TASK_KIND_LABELS.ci, colour: 'pink' },
};
